import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { LocalAttendance } from "@/frontend/utils";
import { readLocalAttendance, writeLocalAttendance } from "@/frontend/utils";
import type { AttendanceStatus, EventRecord } from "@/types/event-attendance";
import type { AttendanceInfoItem } from "./types";

export function useAuthStatus<T>(queryFn: () => Promise<T>) {
	return useQuery<T>({
		queryKey: ["me"],
		queryFn,
		retry: false,
	});
}

export function useAttendanceDraft(storageKey = "attendance_draft") {
	const [localMap, setLocalMap] = useState<LocalAttendance>(() =>
		readLocalAttendance(storageKey),
	);

	useEffect(() => {
		writeLocalAttendance(localMap, storageKey);
	}, [localMap, storageKey]);

	const clearDraft = useCallback(() => setLocalMap({}), []);

	return { localMap, setLocalMap, clearDraft };
}

export function useAttendanceInfo(params: {
	allEvents: EventRecord[];
	loggedIn: boolean;
	myId?: string;
	localMap: LocalAttendance;
}) {
	const { allEvents, loggedIn, myId, localMap } = params;

	return useMemo(() => {
		const map: Record<number, AttendanceInfoItem> = {};

		for (const ev of allEvents) {
			const yes: { userId: string; name: string }[] = [];
			const no: { userId: string; name: string }[] = [];

			(ev.attendance_details ?? []).forEach((d) => {
				const base = { userId: String(d.userid), name: d.displayname };
				if (d.attends === "yes") yes.push(base);
				else if (d.attends === "no") no.push(base);
			});

			let my: AttendanceStatus | undefined;
			if (loggedIn && myId) {
				const mine = (ev.attendance_details ?? []).find(
					(d) => String(d.userid) === String(myId),
				);
				if (mine?.attends === "yes" || mine?.attends === "no") {
					my = mine.attends as AttendanceStatus;
				}
			} else if (!loggedIn) {
				my = localMap[ev.id];
			}

			map[ev.id] = {
				yes,
				no,
				my,
				yesCount: ev.attendance_count?.yes ?? yes.length,
				noCount: ev.attendance_count?.no ?? no.length,
			};
		}
		return map;
	}, [allEvents, myId, loggedIn, localMap]);
}
